import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@shared/ui/button";
import { ArrowRight, ArrowLeft, ShieldCheck, Mail, RefreshCw, Loader2 } from "lucide-react";
import { useParams } from 'react-router-dom';
import { useUtilityInfo, useGenerateOtp, useVerifyOtp } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { useToast } from "@shared/hooks/use-toast";

export const ConfirmEmail = ({ onNext, onPrevious }: any) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    }
  }, [timer]);

  const { tenant, utilityName } = useParams<{ tenant?: string; utilityName?: string }>();
  const { data: utilityInfo } = useUtilityInfo(
    { tenant_alias: tenant || '', utility_alias: utilityName || '' },
    { enabled: !!tenant && !!utilityName }
  );

  const generateOtpMutation = useGenerateOtp();
  const verifyOtpMutation = useVerifyOtp();

  const email = sessionStorage.getItem('forgot_password_email') || '';
  const maskedEmail = email ? `${email.substring(0, 2)}***@${email.split('@')[1]}` : '';

  const handleResend = () => {
    const payload = {
      email,
      remote_utility_id: utilityInfo?.result?.id
    };
    generateOtpMutation.mutate(payload, {
      onSuccess: (res) => {
        const secretKey = 
          res?.otp?.result?.secretKey || 
          res?.result?.otp?.result?.secretKey || 
          res?.data?.otp?.result?.secretKey || 
          res?.secretKey;
          
        if (secretKey) {
          sessionStorage.setItem('forgot_password_secret_key', secretKey);
        } else {
          console.error("Could not find secret_key in resend response:", res);
        }
        setTimer(60);
      },
      onError: (err) => {
        console.error("Failed to resend OTP", err);
      }
    });
  };
  
  const handleVerify = () => {
    const secret_key = sessionStorage.getItem('forgot_password_secret_key') || '';
    const payload = {
      otp,
      secret_key
    };
    verifyOtpMutation.mutate(payload, {
      onSuccess: (res) => {
        console.log("OTP Verified", res);
        onNext();
      },
      onError: (err: any) => {
        console.error("OTP Verification Failed", err);
        const errorMessage = err?.response?.data?.message || err?.message || "Invalid OTP.";
        toast({
          title: "Verification Failed",
          description: errorMessage,
          variant: "destructive"
        });
      }
    });
  };

  return (
    <div className="flex flex-col p-2 items-center text-center">
      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600">
        <Mail className="h-6 w-6" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("Check your email")}</h2>
      <p className="text-gray-500 text-sm mb-6">
        {t("We sent a 6-digit code to ")} <span className="font-bold text-gray-800">{maskedEmail}</span>
      </p>
      
      <div className="flex justify-center mb-8 w-full">
        <div className="w-full max-w-[320px]">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit code"
            className="w-full h-16 border-2 border-gray-200 rounded-2xl bg-gray-50/50 text-center text-2xl tracking-[0.25em] font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-600 outline-none transition-all placeholder:text-gray-400 placeholder:font-medium placeholder:tracking-normal placeholder:text-lg shadow-sm"
          />
        </div>
      </div>

      {timer > 0 ? (
        <div className="text-center text-sm text-gray-500 mb-6 flex items-center justify-center gap-1">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {t("Resend in ")} <span className="font-bold text-gray-800">{timer}s</span>
        </div>
      ) : (
        <div className="text-center mb-6">
          <button onClick={handleResend} className="text-sm text-blue-600 font-semibold hover:text-blue-700 flex items-center justify-center w-full">
            <RefreshCw className="w-4 h-4 mr-1" /> {t("Resend verification code")}
          </button>
        </div>
      )}

      <Button 
        onClick={handleVerify} 
        disabled={verifyOtpMutation.isPending || otp.length !== 6}
        className={`w-full h-12 text-base font-medium mb-6 text-white border-0 ${otp.length === 6 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-[#98B5F6]'}`}
      >
        {verifyOtpMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {t("Verify Code")} <ArrowRight className="ml-2 h-4 w-4" />
      </Button>

      <div className="flex items-center justify-between w-full mb-6 text-sm text-gray-500 font-medium">
        <button onClick={onPrevious} className="hover:text-gray-800 flex items-center">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("Change account")}
        </button>
        <span>{t("Code expires in 10 minutes")}</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-start text-left w-full">
        <ShieldCheck className="h-5 w-5 mr-3 text-green-600 shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500 leading-relaxed font-medium">
          {t("This step verifies you own the email address registered with your utility account. We will never ask for your full password or financial details via email.")}
        </p>
      </div>
    </div>
  );
};
